export abstract class EnvHelper {
  static readonly NODE_ENV = String(process.env.NODE_ENV)
  static readonly BASE_URL = String(process.env.BASE_URL)
  static readonly VUE_APP_ENV = String(process.env.VUE_APP_ENV)
  static readonly REGENT_GRAY_COLOR = String(
    process.env.VUE_APP_REGENT_GRAY_COLOR
  )
  static readonly OCEAN_GREEN_COLOR = String(
    process.env.VUE_APP_OCEAN_GREEN_COLOR
  )
  static readonly AQUAMARINE_COLOR = String(
    process.env.VUE_APP_AQUAMARINE_COLOR
  )
  static readonly ILLUSION_COLOR = String(process.env.VUE_APP_ILLUSION_COLOR)
  static readonly SMALL_SCREEN = Number(process.env.VUE_APP_SMALL_SCREEN)
  static readonly MEDIUM_SCREEN = Number(process.env.VUE_APP_MEDIUM_SCREEN)
  static readonly LARGE_SCREEN = Number(process.env.VUE_APP_LARGE_SCREEN)
  static readonly SWIPER_PER_PAGE_MOBILE = Number(
    process.env.VUE_APP_SWIPER_PER_PAGE_MOBILE
  )
  static readonly SWIPER_PER_PAGE_TABLET = Number(
    process.env.VUE_APP_SWIPER_PER_PAGE_TABLET
  )
  static readonly SWIPER_PER_PAGE_DESKTOP = Number(
    process.env.VUE_APP_SWIPER_PER_PAGE_DESKTOP
  )
  static readonly GASBOT_DISCORD_URL = String(
    process.env.VUE_APP_GASBOT_DISCORD_URL
  )
  static readonly NEO_FEED_URL = String(process.env.VUE_APP_NEO_FEED_URL)
  static readonly DORA_URL = String(process.env.VUE_APP_DORA_URL)
  static readonly ONE_GATE_URL = String(process.env.VUE_APP_ONEGATE_URL)
  static readonly SHRIKE_URL = String(process.env.VUE_APP_SHRIKE_URL)
  static readonly SHRIKE_RPC_URL = String(process.env.VUE_APP_SHRIKE_RPC_URL)
  static readonly VUE_APP_N3_HIDE =
    String(process.env.VUE_APP_N3_SHOW) === 'HIDE'
  static readonly VUE_APP_ROLLBAR_ACCESS_TOKEN = String(
    process.env.VUE_APP_ROLLBAR_ACCESS_TOKEN
  )
  static readonly VUE_APP_BASE_GHOSTMARKET_COLLECTION_URL =
    process.env.VUE_APP_BASE_GHOSTMARKET_COLLECTION_URL
  static readonly VUE_APP_BASE_GHOSTMARKET_URL =
    process.env.VUE_APP_BASE_GHOSTMARKET_URL
  static readonly VUE_APP_RPC_URL = String(process.env.VUE_APP_RPC_URL)
  static readonly VUE_APP_ETH_RPC_URL = String(process.env.VUE_APP_ETH_RPC_URL)
  static readonly VUE_APP_RPC_MAINNET = String(
    process.env.VUE_APP_RPC_MAINNET ?? ''
  )
  static readonly VUE_APP_RPC_TESTNET = String(
    process.env.VUE_APP_RPC_TESTNET ?? ''
  )
  static readonly VUE_APP_ORBIT_SCRIPT_HASH_NEO3_MAINNET = String(
    process.env.VUE_APP_ORBIT_SCRIPT_HASH_NEO3_MAINNET ?? ''
  )
  static readonly VUE_APP_ORBIT_SCRIPT_HASH_NEO3_TESTNET = String(
    process.env.VUE_APP_ORBIT_SCRIPT_HASH_NEO3_TESTNET ?? ''
  )

  private static normalizeNetworkKey(
    networkIdentifier?: string | null
  ): 'NEO3_MAINNET' | 'NEO3_TESTNET' {
    const key = (networkIdentifier ?? '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^N3_/, 'NEO3_')
    return key === 'NEO3_TESTNET' ? 'NEO3_TESTNET' : 'NEO3_MAINNET'
  }

  private static toList(raw: string): string[] {
    return raw
      .split(',')
      .map(url => url.trim())
      .filter(Boolean)
  }

  static rpcUrls(networkIdentifier?: string | null): string[] {
    const map: Record<string, string> = {
      NEO3_MAINNET: this.VUE_APP_RPC_MAINNET,
      NEO3_TESTNET: this.VUE_APP_RPC_TESTNET,
    }
    return this.toList(map[this.normalizeNetworkKey(networkIdentifier)])
  }

  static orbitScriptHash(networkIdentifier?: string | null): string {
    const map: Record<string, string> = {
      NEO3_MAINNET: this.VUE_APP_ORBIT_SCRIPT_HASH_NEO3_MAINNET,
      NEO3_TESTNET: this.VUE_APP_ORBIT_SCRIPT_HASH_NEO3_TESTNET,
    }
    return map[this.normalizeNetworkKey(networkIdentifier)]
  }

  private static readonly BRIDGE_ENV = {
    mainnet: {
      n3Contract: process.env.VUE_APP_BRIDGE_N3_CONTRACT_MAINNET,
      xContract: process.env.VUE_APP_BRIDGE_X_CONTRACT_MAINNET,
      fee: process.env.VUE_APP_BRIDGE_FEE_MAINNET,
      xRpcUrls: process.env.VUE_APP_BRIDGE_X_RPC_MAINNET,
      xChainId: process.env.VUE_APP_BRIDGE_X_CHAIN_ID_MAINNET,
      xExplorerUrl: process.env.VUE_APP_BRIDGE_X_EXPLORER_MAINNET,
      n3ExplorerUrl: process.env.VUE_APP_BRIDGE_N3_EXPLORER_MAINNET,
      xApiUrl: process.env.VUE_APP_BRIDGE_X_API_MAINNET,
      xIndexerUrl: process.env.VUE_APP_BRIDGE_X_INDEXER_MAINNET,
      depositHistoryUrl: process.env.VUE_APP_BRIDGE_DEPOSIT_HISTORY_MAINNET,
      withdrawHistoryUrl: process.env.VUE_APP_BRIDGE_WITHDRAW_HISTORY_MAINNET,
      xGasToken: process.env.VUE_APP_BRIDGE_X_GAS_TOKEN_MAINNET,
      xNeoToken: process.env.VUE_APP_BRIDGE_X_NEO_TOKEN_MAINNET,
      additionalTokenSymbol:
        process.env.VUE_APP_BRIDGE_ADDITIONAL_TOKEN_SYMBOL_MAINNET,
      additionalTokenN3: process.env.VUE_APP_BRIDGE_ADDITIONAL_TOKEN_N3_MAINNET,
      additionalTokenN3Decimals:
        process.env.VUE_APP_BRIDGE_ADDITIONAL_TOKEN_N3_DECIMALS_MAINNET,
      additionalTokenX: process.env.VUE_APP_BRIDGE_ADDITIONAL_TOKEN_X_MAINNET,
      additionalTokenXDecimals:
        process.env.VUE_APP_BRIDGE_ADDITIONAL_TOKEN_X_DECIMALS_MAINNET,
      additionalTokenMin:
        process.env.VUE_APP_BRIDGE_ADDITIONAL_TOKEN_MIN_MAINNET,
    },
    testnet: {
      n3Contract: process.env.VUE_APP_BRIDGE_N3_CONTRACT_TESTNET,
      xContract: process.env.VUE_APP_BRIDGE_X_CONTRACT_TESTNET,
      fee: process.env.VUE_APP_BRIDGE_FEE_TESTNET,
      xRpcUrls: process.env.VUE_APP_BRIDGE_X_RPC_TESTNET,
      xChainId: process.env.VUE_APP_BRIDGE_X_CHAIN_ID_TESTNET,
      xExplorerUrl: process.env.VUE_APP_BRIDGE_X_EXPLORER_TESTNET,
      n3ExplorerUrl: process.env.VUE_APP_BRIDGE_N3_EXPLORER_TESTNET,
      xApiUrl: process.env.VUE_APP_BRIDGE_X_API_TESTNET,
      xIndexerUrl: process.env.VUE_APP_BRIDGE_X_INDEXER_TESTNET,
      depositHistoryUrl: process.env.VUE_APP_BRIDGE_DEPOSIT_HISTORY_TESTNET,
      withdrawHistoryUrl: process.env.VUE_APP_BRIDGE_WITHDRAW_HISTORY_TESTNET,
      xGasToken: process.env.VUE_APP_BRIDGE_X_GAS_TOKEN_TESTNET,
      xNeoToken: process.env.VUE_APP_BRIDGE_X_NEO_TOKEN_TESTNET,
      additionalTokenSymbol:
        process.env.VUE_APP_BRIDGE_ADDITIONAL_TOKEN_SYMBOL_TESTNET,
      additionalTokenN3: process.env.VUE_APP_BRIDGE_ADDITIONAL_TOKEN_N3_TESTNET,
      additionalTokenN3Decimals:
        process.env.VUE_APP_BRIDGE_ADDITIONAL_TOKEN_N3_DECIMALS_TESTNET,
      additionalTokenX: process.env.VUE_APP_BRIDGE_ADDITIONAL_TOKEN_X_TESTNET,
      additionalTokenXDecimals:
        process.env.VUE_APP_BRIDGE_ADDITIONAL_TOKEN_X_DECIMALS_TESTNET,
      additionalTokenMin:
        process.env.VUE_APP_BRIDGE_ADDITIONAL_TOKEN_MIN_TESTNET,
    },
  }

  static bridgeEnv(network: 'mainnet' | 'testnet') {
    return this.BRIDGE_ENV[network] ?? this.BRIDGE_ENV.mainnet
  }

  static toUrlList(raw?: string | null): string[] {
    return this.toList(String(raw ?? ''))
  }

  static readonly VUE_APP_DEFAULT_ETH_DECIMALS = Number(
    process.env.VUE_APP_DEFAULT_ETH_DECIMALS
  )
  static readonly VUE_APP_BNEO_DECIMALS = Number(
    process.env.VUE_APP_BNEO_DECIMALS
  )
  static readonly VUE_APP_NEO_TOTAL_SUPPLY = String(
    process.env.VUE_APP_NEO_TOTAL_SUPPLY
  )
  static readonly VUE_APP_GAS_SCRIPT_HASH = String(
    process.env.VUE_APP_GAS_SCRIPT_HASH
  )
  static readonly VUE_APP_NEO_SCRIPT_HASH = String(
    process.env.VUE_APP_NEO_SCRIPT_HASH
  )
  static readonly VUE_APP_BNEO_SCRIPT_HASH = String(
    process.env.VUE_APP_BNEO_SCRIPT_HASH
  )
  static readonly VUE_APP_CNEO_SCRIPT_HASH = String(
    process.env.VUE_APP_CNEO_SCRIPT_HASH
  )
  static readonly VUE_APP_FLAMINGO_ROUTER_SCRIPT_HASH = String(
    process.env.VUE_APP_FLAMINGO_ROUTER_SCRIPT_HASH
  )
  static readonly VUE_APP_FLAMINGO_PAIR_WHITELIST_SCRIPT_HASH = String(
    process.env.VUE_APP_FLAMINGO_PAIR_WHITELIST_SCRIPT_HASH
  )
  static readonly VUE_APP_FLAMINGO_FACTORY_SCRIPT_HASH = String(
    process.env.VUE_APP_FLAMINGO_FACTORY_SCRIPT_HASH
  )
  static readonly VUE_APP_FLP_BNEO_GAS_PAIR_SCRIPT_HASH = String(
    process.env.VUE_APP_FLP_BNEO_GAS_PAIR_SCRIPT_HASH
  )
  static readonly VUE_APP_NEO_PROXY_SCRIPT_HASH = String(
    process.env.VUE_APP_NEO_PROXY_SCRIPT_HASH
  )
  static readonly VUE_APP_MAX_ATTEMPTS_TO_VALIDATE_TRANSACTION = 25
  static readonly VUE_APP_NO_CANDIDATE_SCRIPT_HASH = String(
    process.env.VUE_APP_NO_CANDIDATE_SCRIPT_HASH
  )
  static readonly VUE_APP_NNT_SCRIPT_HASH = String(
    process.env.VUE_APP_NNT_SCRIPT_HASH
  )
  static readonly VUE_APP_GASBOT_URL = String(process.env.VUE_APP_GASBOT_URL)
  static readonly VUE_APP_OAUTH_GOOGLE_CLIENT_ID = String(
    process.env.VUE_APP_OAUTH_GOOGLE_CLIENT_ID
  )
  static readonly VUE_APP_OAUTH_GOOGLE_REDIRECT_URI = String(
    process.env.VUE_APP_OAUTH_GOOGLE_REDIRECT_URI
  )
  static readonly VUE_APP_RECAPTCHA_SITE_KEY = String(
    process.env.VUE_APP_RECAPTCHA_SITE_KEY ?? ''
  )
  static readonly VUE_APP_DASHBOARD_AUTH_BYPASS =
    process.env.VUE_APP_DASHBOARD_AUTH_BYPASS === 'true'
}
